import { authenticatedFastApiAxiosInstance } from "@/lib/data/http";
import type {
  APIResponse,
  ContactCommunicationSms,
  ContactPhone,
  FirmPhone,
  User,
} from "@/lib/types";
import { baseContactRoute } from "./contact";
import { generateS3ItemPreSignedUrlV1 } from "../s3";

export const retrieveSmsMediaPreview = async (
  s3Key: string,
  signal?: AbortSignal,
) => {
  const { data } = await generateS3ItemPreSignedUrlV1("message-media")({
    clientMethod: "get_object",
    key: s3Key,
  });
  // Read only the first byte to identify extensionless inbound attachments.
  // The player uses the signed URL directly for streaming and seeking.
  const response = await fetch(data.presignedUrl, {
    headers: { Range: "bytes=0-0" },
    signal,
  });
  await response.body?.cancel();
  if (!response.ok) throw new Error("Unable to load this message attachment.");
  return {
    url: data.presignedUrl,
    contentType:
      response.headers
        .get("Content-Type")
        ?.split(";")[0]
        .trim()
        .toLowerCase() ?? "",
  };
};

export function sendSmsV1(data: {
  contactId: string;
  phoneId: string;
  firmPhone: FirmPhone;
  message: string;
  mediaFiles?: {
    s3Key: string;
    contentType: string;
  }[];
  user: User;
}) {
  return authenticatedFastApiAxiosInstance
    .post<APIResponse<ContactPhone>>(
      `${baseContactRoute}/communications/sms/send`,
      {
        contactId: data.contactId,
        phoneId: data.phoneId,
        message: data.message,
        firmPhone: { number: data.firmPhone.number },
        mediaFiles: data.mediaFiles?.map(({ s3Key }) => ({ s3Key })),
      },
    )
    .then((res) => res.data);
}

export type ContactSmsPage = {
  messages: ContactCommunicationSms[];
};

export const retrieveContactSmsV2 = ({
  contactId,
  cursor,
  limit = 100,
  signal,
}: {
  contactId: string;
  cursor?: string;
  limit?: number;
  signal?: AbortSignal;
}) =>
  authenticatedFastApiAxiosInstance
    .get<
      APIResponse<ContactSmsPage>
    >(`${baseContactRoute}/${contactId}/communications/sms`, { params: { cursor, limit }, signal })
    .then((res) => res.data);
